import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  AdminRole,
  AdminSummary,
  AdminUser,
  AuditEntry,
  RoleCreatePayload,
  RoleUpdatePayload,
  UserCreatePayload,
  UserUpdatePayload,
} from './types';

export function useAdminSummary() {
  return useQuery<AdminSummary>({
    queryKey: ['admin', 'summary'],
    queryFn: async () => (await api.get<AdminSummary>('/api/admin/summary')).data,
    refetchInterval: 30_000,
  });
}

export function useAdminUsers(q: string = '') {
  return useQuery<AdminUser[]>({
    queryKey: ['admin', 'users', q],
    queryFn: async () => (await api.get<AdminUser[]>('/api/admin/users', { params: q ? { q } : {} })).data,
  });
}

export function useAdminRoles() {
  return useQuery<AdminRole[]>({
    queryKey: ['admin', 'roles'],
    queryFn: async () => (await api.get<AdminRole[]>('/api/admin/roles')).data,
  });
}

export function useAdminAudit(opts: { limit?: number; action?: string; actor_email?: string; severity_gte?: number } = {}) {
  return useQuery<AuditEntry[]>({
    queryKey: ['admin', 'audit', opts],
    queryFn: async () => (await api.get<AuditEntry[]>('/api/admin/audit', { params: opts })).data,
    refetchInterval: 15_000,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UserCreatePayload) =>
      (await api.post<AdminUser>('/api/admin/users', payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'summary'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UserUpdatePayload }) =>
      (await api.patch<AdminUser>(`/api/admin/users/${id}`, data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => (await api.delete(`/api/admin/users/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'summary'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RoleCreatePayload) =>
      (await api.post<AdminRole>('/api/admin/roles', payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: RoleUpdatePayload }) =>
      (await api.patch<AdminRole>(`/api/admin/roles/${id}`, data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => (await api.delete(`/api/admin/roles/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

/**
 * Adjust a user's fierros balance. Positive amount = grant, negative = revoke.
 * Backend rejects 0 and any operation that would push the balance below zero.
 *
 * Used by the inline +/- controls on the admin Users tab.
 */
export function useAdjustUserTokens() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      user_id,
      amount,
      note,
    }: {
      user_id: number;
      amount: number;
      note?: string;
    }) =>
      (
        await api.post('/api/billing/admin/grant-tokens', {
          user_id,
          amount,
          note: note ?? '',
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
      qc.invalidateQueries({ queryKey: ['admin', 'billing'] });
    },
  });
}
